import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment/moment';
import Title from '../../../../commonComponents/Title';
import translate from '../../../../commonComponents/translate';
import ContentContainer from '../../../../commonComponents/pageLayout/ContentContainer';
import CareerFilters from './CareerFilters';
import GlobalSummary from './GlobalSummary';
import SubHeader from '../../../../commonComponents/pageLayout/SubHeader';
import Notifications from '../../../../services/notifications';
import Loader from '../../../../services/loader';
import TopicBanner from '../../../../commonComponents/pageLayout/header/TopicBanner';
import PayplanTabs from './PayplanTabs';
import FastStartBonus from './FastStartBonus';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import { getStartDateAndEndDateInterval } from '../../../../services/dateUtilityFunctions';

function MyCareer(props) {
  const { t, _csrf } = props;

  const loader = useRef(new Loader('#laborigin-my-career'));
  const [isLoading, setLoading] = useState(false);
  const [levelObjectives, setLevelObjectives] = useState([]);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const [globalSummaryData, setGlobalSummaryData] = useState({
    userId: '',
    user: null,
    level: 0,
    forecastLevel: 0,
    lastMonthLevel: 0,
    isActive: true,
    totalCommissions: 0,
    margin: 0,
    successBonus: 0,
    fastStartBonus: 0,
    vatThreshold: 0,
  });

  const [bonusData, setBonusData] = useState({
    totalElitePartnerBonus: 0,
    totalEliteManagerBonus: 0,
    totalInfinitePartnerBonus: 0,
    totalInfiniteManagerBonus: 0,
    totalPersonalVA: 0,
    totalGroupVA: 0,
    totalMatchingBonus: 0,
    totalEliteBonus: 0,
    eliteBonusThreshold: 0,
    infiniteBonusThreshold: 0,
    elitePartnerMinimumBonus: 0,
    eliteManagerMinimumBonus: 0,
    totalUniLevel: 0,
    achievedFastStart1: false,
    achievedFastStart2: false,
    achievedFastStart3: false,
    recruited: 0,
    percentageEliteManager: 0,
    totalSale: 0,
    totalBusinessBonus: 0,
    totalInfiniteBusinessBonus: 0,
    infiniteBusinessThreshold: 0,
    eliteBusinessMinimumBonus: 0,
    currentSaleCommission: 0,
  });

  const { organization } = useContext(ConfigsContext);
  const { directSelling } = organization;

  // List — initial range must use the same plan-timezone boundaries as the
  // CareerFilters presets (BRAVON-6325), not plain UTC startOf('month').
  const [initialStartDate, initialEndDate] =
    getStartDateAndEndDateInterval(directSelling);
  const startOfMonth = initialStartDate.valueOf();
  const today = initialEndDate.valueOf();

  const { loggedUser } = useContext(LoggedUserContext);
  const { _id: loggedUserId } = loggedUser;

  const [filters, setFilters] = useState({
    user: loggedUserId,
    userNumber: null,
    startDate: startOfMonth,
    endDate: today,
  });

  const [collaboratorSince, setCollaboratorSince] = useState(today);

  const handleFiltersChange = useCallback(
    field => value =>
      setFilters(prevState => ({ ...prevState, [field]: value })),
    [setFilters],
  );
  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({
      ...prevState,
      startDate,
      endDate,
    }));
  };

  const handleGetGlobalSummary = useCallback(() => {
    axios
      .get('/api/tailor-made/laborigin/summary/global', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setGlobalSummaryData(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleGetObjectives = useCallback(() => {
    axios
      .get('/api/tailor-made/laborigin/level/objectives', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setLevelObjectives(response.data);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleBonus = useCallback(() => {
    axios
      .get('/api/tailor-made/laborigin/summary/bonus', {
        params: filters,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setBonusData(response.data);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters]);

  const handleGetCollaboratorSince = useCallback(() => {
    axios
      .get(`/api/user/direct-selling/collaborator-date/${filters.user}`, {
        params: {},
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        setCollaboratorSince(response.data?.collaboratorDate);
      })
      .catch(err => {
        console.log(err);
        Notifications.showErrorFromRequest(err, 'error_fetchGlobalSummaryData');
      });
  }, [filters.user]);

  useEffect(() => {
    handleGetGlobalSummary();
    handleGetObjectives();
    handleBonus();
    handleGetCollaboratorSince();
  }, [filters]);

  const totalBusiness =
    bonusData.totalInfiniteBusinessBonus +
    bonusData.totalSale +
    bonusData.totalBusinessBonus;

  const totalElite =
    bonusData.totalElitePartnerBonus +
    bonusData.totalEliteManagerBonus +
    bonusData.totalInfinitePartnerBonus +
    bonusData.totalInfiniteManagerBonus;

  const totalExecutive = bonusData.totalMatchingBonus + bonusData.totalUniLevel;

  return (
    <Title title={t('header_myCareer')}>
      <ContentContainer>
        <SubHeader title={t('header_myCareer')} />
        <TopicBanner />
        <div className="m-content" id="laborigin-my-career">
          {!isLoading && (
            <div className="full-page-container-with-subheader">
              <CareerFilters
                t={t}
                onDateChange={handleDateChange}
                onFilterChange={handleFiltersChange}
                filters={filters}
              />
              <div className="career-summaries">
                <GlobalSummary
                  t={t}
                  data={globalSummaryData}
                  bestEliteBranch={Math.max(
                    totalBusiness,
                    totalElite,
                    totalExecutive,
                  )}
                />
              </div>
              <PayplanTabs data={bonusData} levelObjectives={levelObjectives} />
              <FastStartBonus
                collaboratorDate={collaboratorSince}
                achievedFastStart1={bonusData.achievedFastStart1}
                achievedFastStart2={bonusData.achievedFastStart2}
                achievedFastStart3={bonusData.achievedFastStart3}
              />
            </div>
          )}
        </div>
      </ContentContainer>
    </Title>
  );
}

MyCareer.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
};

export default translate(MyCareer);
